import type { Theme } from "@mui/system";
import type { ComicDrawerProps } from "@vapor/react-contrib-tax-compliance/ComicDrawer/ComicDrawer";
import { ArrowLeft } from "@vapor/react-icons";
import { IconButton, useMediaQuery } from "@vapor/react-material";
import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TaxSubmissionF24Previewer } from "../components/tax-submission-f24-previewer/TaxSubmissionF24Previewer";
import type { DetailDrawerParams } from "./useDetailDrawer";

export interface F24PreviewDrawerParams {
    taxSubmissionId: string;
    taxPayerId: string;
    taxPayerName: string;
    taxPayerPosition: number;
    delegationPosition?: number;
}

export const useF24PreviewDrawer = (
    params: F24PreviewDrawerParams | undefined,
    setParams: Dispatch<SetStateAction<F24PreviewDrawerParams | undefined>>,
    setSelectedTaxSubmission: Dispatch<
        SetStateAction<DetailDrawerParams | undefined>
    >,
): ComicDrawerProps | undefined => {
    const { t } = useTranslation();
    const isXlScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.up(theme.breakpoints.values.xl),
    );

    return useMemo(() => {
        if (!params) return undefined;

        return {
            content: <TaxSubmissionF24Previewer {...params} />,
            headerLeft: (
                <IconButton
                    onClick={() => setParams(undefined)}
                    sx={{ padding: 0 }}
                >
                    <ArrowLeft color="interactive" />
                </IconButton>
            ),
            headerTitle: t(
                "views.home.components.tax_submission_detail_summary.preview_f24_title",
                {
                    taxpayer: params.taxPayerName,
                },
            ),
            onClickCloseButton: () => {
                setParams(undefined);
                setSelectedTaxSubmission(undefined);
            },
            open: true,
            width: isXlScreen ? "45%" : "65%",
        };
    }, [params, setParams, setSelectedTaxSubmission, t, isXlScreen]);
};
